import { api_fetch, HttpError, HttpResponse } from "@/api/client"
import { Result } from "@/utils"

export interface ProductSearchResult {
  id: string
  name: string
  sku?: string
  lifecycle_stage?: string
  is_pack: boolean
  is_sellable: boolean
  units_per_case?: number
  unit_size?: string
  /** Catalog list price per case, normalized to `units_per_case` (unlike raw `prices`). */
  list_price?: number
  /** Catalog cost of goods per case, when the brand loaded one. */
  cogs?: number
  /** Shelf price per consumer unit, when the brand loaded one. */
  shopping_price?: number
  product_line_id?: string
  product_line?: string
  product_category_id?: string
  product_category?: string
  product_groups: string[]
  /** Every identifier, in no particular order: case GTINs and distributor item numbers too. */
  identifiers: string[]
  /** The product's UPC — what a plan's `sku:<upc>` stable key carries. */
  upc?: string | null
  aliases: string[]
  prices: ProductPriceSummary[]
}

/**
 * The identity to key a plan row on. Never `identifiers[0]`: that list is unordered, and a
 * 14-digit case GTIN sorting ahead of the UPC keyed new rows off the plan's existing ones.
 */
export function product_upc(product: Pick<ProductSearchResult, "upc" | "identifiers">): string | undefined {
  return product.upc || product.identifiers?.[0] || undefined
}

export interface ProductCategory {
  id: string
  name: string
  alternative_name?: string
  pricing_enabled: boolean
  product_group_margin: number
  display_order: number
  is_active: boolean
}

export interface ProductLine {
  id: string
  category_id?: string
  name: string
  alternative_name?: string
  display_order: number
  is_active: boolean
}

export interface UpdateProductRequest {
  name: string
  description?: string
  sku?: string
  lifecycle_stage?: string
  is_pack: boolean
  is_sellable: boolean
  units_per_case?: number
  unit_size?: string
  default_uom?: string
  product_line_id?: string
  product_category_id?: string
  product_group_ids?: string[]
  identifiers?: string[]
  aliases?: string[]
}

export interface ProductPriceSummary {
  customer_name: string
  retailer_name?: string
  unit_price: number
  currency: string
  effective_start?: string
  effective_end?: string
}

export interface ProductMatchInput {
  upc?: string | null
  product_number?: string | null
  inventory_number?: string | null
  product_description?: string | null
}

export interface ProductMatchResult {
  product_id?: string
  product_line_id?: string
  product_category_id?: string
  product_name?: string
  product_line?: string
  product_category?: string
  match_kind: string
  confidence: number
}

export interface ProductMatchResponse {
  matches: ProductMatchResult[]
}

export interface SearchProductsOptions {
  /**
   * `false` leaves `prices` empty on every result. A product can carry several hundred customer
   * prices; a picker that only shows names and identifiers should not download them.
   */
  include_prices?: boolean
  /** Only products of this product line (by name); blank or absent searches the whole catalog. */
  product_line?: string
}

export async function searchProducts(
  query: string,
  limit = 25,
  options: SearchProductsOptions = {}
): Promise<Result<HttpResponse<ProductSearchResult[]>, HttpError>> {
  return api_fetch<ProductSearchResult[]>("/product-catalog/products", {
    params: {
      q: query,
      limit,
      include_prices: options.include_prices,
      product_line: options.product_line?.trim() ? options.product_line.trim() : undefined,
    },
  })
}

export async function matchProducts(
  rows: ProductMatchInput[]
): Promise<Result<HttpResponse<ProductMatchResponse>, HttpError>> {
  return api_fetch<ProductMatchResponse>("/product-catalog/match", {
    method: "POST",
    body: { rows },
  })
}

export async function listProductCategories(): Promise<Result<HttpResponse<ProductCategory[]>, HttpError>> {
  return api_fetch<ProductCategory[]>("/product-catalog/categories")
}

export async function listProductLines(): Promise<Result<HttpResponse<ProductLine[]>, HttpError>> {
  return api_fetch<ProductLine[]>("/product-catalog/lines")
}

export async function updateProduct(
  productId: string,
  body: UpdateProductRequest
): Promise<Result<HttpResponse<ProductSearchResult>, HttpError>> {
  return api_fetch<ProductSearchResult>(`/product-catalog/products/${productId}`, {
    method: "PATCH",
    body,
  })
}

/** The picked products, with their customer prices, for the promotion form's deal math. */
export async function fetchProductsByIds(ids: string[]): Promise<Result<HttpResponse<ProductSearchResult[]>, HttpError>> {
  return api_fetch<ProductSearchResult[]>("/product-catalog/products", {
    params: { ids: ids.join(","), limit: Math.min(Math.max(ids.length, 1), 100), include_prices: true },
  })
}

export function getProductDisplayLabel(product: ProductSearchResult): string {
  const details = [product.product_line, product.product_category, product.unit_size].filter(Boolean)
  return details.length > 0 ? `${product.name} (${details.join(" / ")})` : product.name
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("getProductDisplayLabel", () => {
    it("includes hierarchy details when available", () => {
      expect(
        getProductDisplayLabel({
          id: "p1",
          name: "Calamansi 6-pack case",
          is_pack: true,
          is_sellable: true,
          product_line: "Calamansi",
          product_category: "6-pack",
          product_groups: ["6-pack"],
          unit_size: "12oz",
          identifiers: [],
          aliases: [],
          prices: [],
        })
      ).toBe("Calamansi 6-pack case (Calamansi / 6-pack / 12oz)")
    })
  })
  describe("product_upc", () => {
    it("is the product's UPC, not the first of its unordered identifiers", () => {
      // Sanzo's Yuzu 6-pack: the case GTIN comes back ahead of the UPC.
      expect(product_upc({ upc: "840269200243", identifiers: ["10840269200240", "2208042", "840269200243"] })).toBe("840269200243")
    })

    it("falls back to the first identifier for a product with no UPC on file", () => {
      expect(product_upc({ upc: null, identifiers: ["sz-001"] })).toBe("sz-001")
      expect(product_upc({ identifiers: [] })).toBeUndefined()
    })
  })
}
